import React, { useState } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { verifySecretaryApi, type SecretaryUser } from '../../../shared/api/secretaryApi';
import { saveSession } from '../../../shared/auth/portalSession';
import RegistrationModal from './RegistrationModal';

interface LoginProps {
  onLoginSuccess: (user: SecretaryUser) => void;
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string>('');

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setLoading(true);
      setError(null);

      // The credential is a JWT ID token
      const idToken = credentialResponse.credential;

      // Decode the JWT to get user info and expiry
      const payload = JSON.parse(decodeURIComponent(Array.from(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')), c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')));
      const email = payload.email;
      const name = payload.name;
      const exp = payload.exp; // Expiry timestamp (seconds since epoch)

      // Check if token is already expired
      const now = Math.floor(Date.now() / 1000);
      if (exp <= now) {
        setError('הטוקן פג תוקף. אנא נסה שוב.');
        setLoading(false);
        return;
      }

      setUserEmail(email);
      setUserName(name);
      setAccessToken(idToken);

      // Verify with backend
      const response = await verifySecretaryApi(idToken);

      if (response.exists && response.user) {
        saveSession('secretary', { user: response.user, token: response.token!, expiresAt: response.expiresAt! });
        setAccessToken('');
        onLoginSuccess(response.user);
      } else {
        // New user, show registration modal
        setShowRegistration(true);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'התחברות נכשלה. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('התחברות באמצעות Google נכשלה. אנא נסה שוב.');
  };

  const handleRegistrationComplete = (user: SecretaryUser, token: string, expiresAt: number) => {
    saveSession('secretary', { user, token, expiresAt });
    setAccessToken('');
    setShowRegistration(false);
    onLoginSuccess(user);
  };

  const handleRegistrationCancel = () => {
    setShowRegistration(false);
    setUserEmail('');
    setUserName('');
    setAccessToken('');
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform">
                  <svg className="w-14 h-14 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                {/* Decorative pulse */}
                <div className="absolute inset-0 bg-teal-400 rounded-2xl animate-ping opacity-20"></div>
              </div>
            </div>
            
            <h1 className="text-3xl font-bold text-slate-800 mb-2">מרכז מונא הרפואי - תורים</h1>
            <p className="text-lg text-teal-600 font-medium mb-1">כפר כנא</p>
            <p className="text-slate-500 text-sm">פורטל מזכירות</p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 border border-slate-100">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                התחברות למערכת
              </h2>
              <p className="text-slate-500 text-sm">
                התחבר באמצעות חשבון Google
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-center text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                text="signin_with"
                locale="he"
              />
            </div>

            {loading && (
              <div className="mt-6 flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-teal-600 border-t-transparent"></div>
                <span className="text-sm text-slate-600">מתחבר...</span>
              </div>
            )}

            {/* Info Footer */}
            <div className="mt-8 pt-6 border-t border-slate-100">
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>משתמשים חדשים יתבקשו למלא פרטים נוספים</span>
              </div>
            </div>
          </div>

          {/* Security Badge */}
          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur rounded-full shadow-sm border border-slate-100">
              <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-xs text-slate-600 font-medium">חיבור מאובטח באמצעות Google OAuth</span>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Modal */}
      {showRegistration && (
        <RegistrationModal
          email={userEmail}
          name={userName}
          accessToken={accessToken}
          onComplete={handleRegistrationComplete}
          onCancel={handleRegistrationCancel}
          onCredentialExpired={() => { handleRegistrationCancel(); setError('תוקף ההתחברות פג. יש להתחבר שוב עם Google.'); }}
        />
      )}
    </GoogleOAuthProvider>
  );
};

export default Login;
